import { Component, OnInit, TemplateRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ISession } from '../../../models/session';
import { ISpecial, levelType } from '../../../models/special';
import { UsersService } from './users.service';
import { TranslateService } from '@ngx-translate/core';
import { FormBuilder, Validators, FormControl } from '@angular/forms';
import { Observable } from 'rxjs/Observable';
import { map, first } from 'rxjs/operators';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BsModalRef } from 'ngx-bootstrap/modal/bs-modal-ref.service';
import { mergeAll, omit, objOf, compose, invertObj } from 'ramda/es';
import { SessionsService } from '../sessions.service';
import { ConfirmationModalComponent } from '../../modals/confirmation/confirmation-modal.component';

function forbiddenSpaceValidator(control: FormControl) {
  // validator must return an observable
  return Observable.of(/\s/.test(control.value)).pipe(
    map(result => (result ? { forbiddenSpace: { value: control.value } } : null))
  );
}

@Component({
  selector: 'session-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss']
})
export class SessionsUsersComponent implements OnInit {
  public defaultAvatar = './assets/images/default-avatar.png';
  public session: ISession;
  public users: ISpecial[];
  public type: 'moderator' | 'guest';
  public selectedUser: ISpecial;
  public showPass: boolean = false;
  public modalRef: BsModalRef;
  public links = {};
  public currentTime = +new Date();
  public rtypes = invertObj(levelType);
  public userForm = this.fb.group({
    username: ['', Validators.required, forbiddenSpaceValidator],
    firstname: ['', Validators.required],
    lastname: ['', Validators.required],
    avatar: [''],
    // password: ['', Validators.required],
    // passwordVerify: ['', Validators.required]
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public fb: FormBuilder,
    private translateService: TranslateService,
    private usersService: UsersService,
    private modalService: BsModalService,
    private sessionsService: SessionsService
  ) {}

  private disableCache(avatar) {
    return `${avatar}?ts=${+new Date()}`;
  }

  private filterUsers(users: ISpecial[]): ISpecial[] {
    return users.filter(user => !!this.rtypes[user.type]);
  }

  ngOnInit() {
    this.route.parent.data.map(route => route.data).subscribe((session: ISession) => {
      this.session = session;
      this.links = mergeAll(
        session.urls
          .filter(f => Object.keys(levelType).indexOf(f.name) > -1)
          .map(d => compose(objOf(levelType[d.name]))(d.link))
      );
      this.usersService.getSpecialUsers(this.session.id).subscribe((users: ISpecial[]) => {
        const types = {'S':100, 'M':80, 'O':70, 'U':60};
        const sortByType = (u1, u2) => (types[u1.type] == types[u2.type] ? 0 : types[u1.type] < types[u2.type] ? 1 : -1);
        this.users = this.filterUsers(users.map(user => {
          user.avatar = this.disableCache(user.avatar);
          return user;
        }).sort(sortByType));
      });
    });
  }

  openModal(template: TemplateRef<any>, user: any) {
    this.selectedUser = user;
    this.showPass = false;
    this.modalRef = this.modalService.show(template, { class: 'inmodal' });
  }

  closeForm() {
    this.userForm.reset();
    this.modalRef.hide();
  }

  importImage(imageData) {
    this.userForm.patchValue({ avatar: imageData.data });
  }

  add(): void {
    if (this.userForm.value.password !== this.userForm.value.passwordVerify) {
      toastr.error(this.translateService.instant('action.passwordError'));
      return;
    }
    this.usersService
      .addSpecialUsers(this.session.id, this.type, omit(['passwordVerify'], this.userForm.value))
      .subscribe(() => {
        this.usersService.getSpecialUsers(this.session.id).subscribe(updatedUsers => {
          this.users = this.filterUsers(updatedUsers);
          this.closeForm();
        });
      });
  }

  edit() {
    // if (
    //   this.userForm.value.password &&
    //   !!this.userForm.value.password.length &&
    //   this.userForm.value.password !== this.userForm.value.passwordVerify
    // ) {
    //   return toastr.error(this.translateService.instant('action.passwordError'));
    // }
    // this.userForm.value.password = this.userForm.value.password || '';
    // this.userForm.value.username = this.selectedUser.login;

    // if (this.selectedUser.login == 'mod') {
    //   this.userForm.value.password = this.selectedUser.password;
    // }

    this.usersService
      // .editSpecialUsers(this.session.id, omit(['passwordVerify'], this.userForm.value))
      .editSpecialUsers(this.session.id, this.userForm.value)
      .subscribe(() => {
        this.usersService.getSpecialUsers(this.session.id).subscribe(updatedUsers => {
          this.users = this.filterUsers(updatedUsers.map(user => {
            // force reload of updated user avatar
            if (user.login === this.selectedUser.login) {
              // we need to use settimeout because eurostudio answers the request before having updated the avatar
              setTimeout(() => (user.avatar = this.disableCache(user.avatar)), 2000);
            }
            return user;
          }));
          this.closeForm();
        });
        this.sessionsService.setComplete(this.session, 'manage_my_event');
      });
  }

  deleteUser(user: ISpecial) {
    const modal = this.modalService.show(ConfirmationModalComponent);
    (modal.content as ConfirmationModalComponent).showConfirmationModal(
      this.translateService.instant('users.delete_confirm', {
        login: user.login,
        type: this.translateService.instant('users.' + this.rtypes[user.type] + '.title')
      })
    );

    (modal.content as ConfirmationModalComponent).onClose
      .pipe(first())
      .subscribe(confirmed => {
        if (!confirmed) {
          return;
        }
        this.usersService.deleteSpecialUser(this.session.id, user).subscribe(() => {
          this.users = this.users.filter(u => u.id !== user.id);
          if (this.selectedUser && this.selectedUser.id === user.id) {
            this.selectedUser = null;
            this.userForm.reset();
          }
        });
      });
  }

  getUsersByType(type) {
    return this.users.filter(user => user.type === type);
  }

  onCopySuccess() {
    toastr.success(this.translateService.instant('sessions.edit.link.copied_success'));
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/sessions/users/users.component.ts